import React from 'react';
import { X, Save, RefreshCw, ExternalLink, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

// Return a carrier's tracking URL for a given number (mirrors backend build_tracking_url).
const buildTrackingUrl = (trackingNumber, carrier) => {
  if (!trackingNumber) return '';
  const c = (carrier || '').trim().toUpperCase();
  const map = {
    USPS: `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`,
    UPS: `https://www.ups.com/track?tracknum=${trackingNumber}`,
    FEDEX: `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`,
    DHL: `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${trackingNumber}`,
  };
  return map[c] || `https://www.google.com/search?q=${trackingNumber}+tracking`;
};

// Best-effort auto-detect carrier from the tracking number format.
const detectCarrier = (tn) => {
  if (!tn) return '';
  const raw = tn.replace(/\s+/g, '').toUpperCase();
  if (/^1Z[0-9A-Z]{16}$/.test(raw)) return 'UPS';
  if (/^96\d{20}$/.test(raw) || /^\d{15}$/.test(raw) || /^\d{12}$/.test(raw)) return 'FedEx';
  if (/^\d{10}$/.test(raw)) return 'DHL';
  if (/^(94|93|92|94|95)\d{20}$/.test(raw) || /^(EA|EC|CP|EE|LK|RA)\w{7,9}US$/.test(raw) || /^\d{22}$/.test(raw) || /^\d{20}$/.test(raw)) return 'USPS';
  return '';
};

export const OrderFulfillmentModal = ({
  order,
  form,
  setForm,
  updating,
  onClose,
  onSubmit,
}) => {
  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // Handler that also tries to auto-fill carrier if not chosen yet
  const handleTrackingChange = (value) => {
    setForm((prev) => {
      const next = { ...prev, tracking_number: value };
      if (!prev.carrier || prev.carrier === '') {
        const detected = detectCarrier(value);
        if (detected) next.carrier = detected;
      }
      return next;
    });
  };

  const trackingPreviewUrl =
    form.tracking_number && form.carrier
      ? buildTrackingUrl(form.tracking_number, form.carrier)
      : '';

  const willSendEmail =
    form.send_notification &&
    (form.status !== (order.status || 'pending') ||
      (form.status === 'shipped' &&
        form.tracking_number &&
        form.tracking_number !== (order.tracking_number || '')));

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="bg-purple-900 border-purple-600 max-w-lg w-full my-8">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              Fulfill Order
            </CardTitle>
            <p className="text-purple-300 text-xs mt-1">{order.order_number}</p>
          </div>
          <Button variant="ghost" onClick={onClose} data-testid="close-order-modal">
            <X className="w-5 h-5 text-purple-300" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-purple-200">Status</Label>
            <select
              value={form.status}
              onChange={(e) => setField('status', e.target.value)}
              className="w-full bg-purple-800 border border-purple-600 text-white rounded-md p-2"
              data-testid="order-status-select"
            >
              <option value="pending">Pending</option>
              <option value="processing">Processing (anointing)</option>
              <option value="shipped">Shipped (on the way ✦)</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <Label className="text-purple-200">Tracking Number</Label>
            <Input
              value={form.tracking_number}
              onChange={(e) => handleTrackingChange(e.target.value)}
              placeholder="e.g. 9400111899223193437123"
              className="bg-purple-800 border-purple-600 text-white"
              data-testid="tracking-number-input"
            />
            <p className="text-purple-400 text-xs mt-1 italic">
              We&apos;ll auto-detect the carrier from most tracking-number formats.
            </p>
          </div>

          <div>
            <Label className="text-purple-200">Carrier</Label>
            <select
              value={form.carrier}
              onChange={(e) => setField('carrier', e.target.value)}
              className="w-full bg-purple-800 border border-purple-600 text-white rounded-md p-2"
              data-testid="carrier-select"
            >
              <option value="">Select carrier...</option>
              <option value="USPS">USPS</option>
              <option value="UPS">UPS</option>
              <option value="FedEx">FedEx</option>
              <option value="DHL">DHL</option>
            </select>
          </div>

          {/* Live tracking-link preview */}
          {trackingPreviewUrl && (
            <div className="rounded-lg bg-yellow-500/10 border border-yellow-400/40 p-3">
              <p className="text-yellow-300 text-xs uppercase tracking-wider font-medium mb-2">
                ✦ Tracking Link Preview
              </p>
              <a
                href={trackingPreviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-yellow-200 hover:text-yellow-100 text-sm break-all inline-flex items-center gap-1"
                data-testid="tracking-preview-link"
              >
                {trackingPreviewUrl}
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
              <p className="text-purple-300 text-xs mt-2 italic">
                Customers will see a beautiful gold &ldquo;Track Your Package&rdquo; button in their email.
              </p>
            </div>
          )}

          <div>
            <Label className="text-purple-200">Notes (optional)</Label>
            <Input
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              placeholder="Internal notes about this fulfillment..."
              className="bg-purple-800 border-purple-600 text-white"
            />
          </div>

          <div className="flex items-start gap-3 pt-2 border-t border-purple-700">
            <input
              type="checkbox"
              id="send_notification"
              checked={form.send_notification}
              onChange={(e) => setField('send_notification', e.target.checked)}
              className="mt-1 rounded border-purple-600"
            />
            <Label htmlFor="send_notification" className="text-purple-200 cursor-pointer text-sm leading-relaxed">
              Send sacred email notification to customer
              {willSendEmail && (
                <span className="block mt-1 text-yellow-300 text-xs">
                  ✦ Email will be sent: &ldquo;Your Sacred Drops of Love Are On the Way&rdquo;
                </span>
              )}
            </Label>
          </div>

          <Button
            onClick={onSubmit}
            disabled={updating}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-purple-900 font-semibold"
            data-testid="update-order-btn"
          >
            {updating ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Update Order &amp; Notify Customer
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
